import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function WhitelistAdmin() {
  const toast = useToast();
  const [data, setData] = useState({ players: [], server_status: [] });
  const [servers, setServers] = useState([]);
  const [playerid, setPlayerid] = useState("");
  const [check, setCheck] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    api.get("/live/servers").then(({ data }) => setServers(data.servers || [])).catch(() => {});
  }, []);

  const load = () => api.get("/admin/whitelist").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/whitelist/add", { playerid });
      toast.success(`Added ${playerid}`);
      setPlayerid("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (pid) => {
    if (!window.confirm(`Remove ${pid} from whitelist?`)) return;
    try { await api.delete(`/admin/whitelist/${encodeURIComponent(pid)}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (name, enabled) => {
    try {
      await api.put(`/admin/whitelist/${encodeURIComponent(name)}/status?enabled=${enabled}`);
      toast.success(`${name}: whitelist ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doCheck = async () => {
    if (!check) return;
    try { const { data } = await api.get(`/admin/whitelist/check/${encodeURIComponent(check)}`); setCheckResult(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statusByServer = Object.fromEntries((data.server_status || []).map((s) => [s.server_name, !!s.enabled]));

  return (
    <div className="space-y-4 fade-up" data-testid="whitelist-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Whitelist</h3>
        <p className="text-xs text-[var(--muted)]">
          When enabled for a server, only listed player UIDs can join. Mirrors the bot's <code>/whitelist</code> commands.
        </p>
      </div>

      <div className="glass-card p-5">
        <h3 className="label mb-3">Per-server Enforcement</h3>
        <ul className="space-y-2">
          {servers.map((s) => {
            const name = s.name || s.slug;
            const on = statusByServer[name];
            return (
              <li key={s.slug} className="flex items-center justify-between text-sm bg-black/20 rounded p-3">
                <span>{name}</span>
                <button onClick={() => setStatus(name, !on)} className={`btn btn-sm ${on ? "btn-danger" : ""}`} data-testid={`wl-toggle-${s.slug}`}>
                  {on ? "Disable" : "Enable"}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <form onSubmit={add} className="glass-card p-5 space-y-3" data-testid="wl-add-form">
          <h3 className="label">Add Player UID</h3>
          <Field label="Player UID"><input className="input font-mono text-xs" required value={playerid} onChange={(e) => setPlayerid(e.target.value)} data-testid="wl-add-input" /></Field>
          <button className="btn" data-testid="wl-add-submit">Add</button>
        </form>

        <div className="glass-card p-5 space-y-3">
          <h3 className="label">Check whitelisted?</h3>
          <Field label="Player UID"><input className="input font-mono text-xs" value={check} onChange={(e) => setCheck(e.target.value)} data-testid="wl-check-input" /></Field>
          <button onClick={doCheck} className="btn btn-secondary" data-testid="wl-check-submit">Check</button>
          {checkResult && (
            <div className="text-sm" data-testid="wl-check-result">
              <span className="font-mono">{checkResult.playerid}</span>:{" "}
              <span className={checkResult.whitelisted ? "text-[var(--teal)]" : "text-[var(--danger)]"}>
                {checkResult.whitelisted ? "✓ whitelisted" : "✗ not whitelisted"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="label mb-3">Whitelisted Players ({data.players.length})</h3>
        {data.players.length === 0 && <p className="text-sm text-[var(--muted)]">No players whitelisted.</p>}
        <ul className="divide-y divide-white/5 max-h-96 overflow-y-auto">
          {data.players.map((p) => (
            <li key={p.playerid} className="flex items-center justify-between py-2 text-sm">
              <span className="font-mono text-xs">{p.playerid}</span>
              <button onClick={() => remove(p.playerid)} className="btn btn-sm btn-danger" data-testid={`wl-remove-${p.playerid}`}>Remove</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
